import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, BookOpen, Calculator, Globe2, ArrowRight, Lock, Volume2, VolumeX, Play, Pause } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { debug, error as logError } from '../../utils/logger';
import { DjayAvatar, DjayBubble, DjayMeesterLogo, getDjayWelcomeMessage } from '../../mascot';
interface WelcomeScreenProps {
  onLoadDemo: () => void;
  onLogin: () => void;
  onRegister: () => void;
}
function FloatingIcon({
  children,
  delay,
  x,
  y,
  duration = 8
}: {
  children: React.ReactNode;
  delay: number;
  x: string;
  y: string;
  duration?: number;
}) {
  return <motion.div className="absolute text-orange-400/30 pointer-events-none select-none z-0" style={{
    left: x,
    top: y
  }} initial={{
    opacity: 0,
    scale: 0.5
  }} animate={{
    opacity: [0, 0.8, 0.4, 0.8],
    scale: [0.5, 1.2, 0.9, 1.2],
    y: [0, -20, 0, -10, 0],
    rotate: [0, 10, -10, 5, 0]
  }} transition={{
    duration: duration,
    delay,
    repeat: Infinity,
    ease: 'easeInOut'
  }} data-id="element-552">
      {children}
    </motion.div>;
}
export function WelcomeScreen({
  onLoadDemo,
  onLogin,
  onRegister
}: WelcomeScreenProps) {
  const welcomeMsg = getDjayWelcomeMessage();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(false); // Start unmuted as requested
  const [isPlaying, setIsPlaying] = useState(true);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = 0.6;
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          // Browser blocked autoplay with audio. Mute it and play, show unmute prompt.
          debug('Autoplay with audio blocked, falling back to muted autoplay.');
          setIsMuted(true);
          setAutoplayBlocked(true);
          if (videoRef.current) {
            videoRef.current.muted = true;
            videoRef.current.play().catch(e => logError('Even muted autoplay failed', e));
          }
        });
      }
    }
  }, []);
  const toggleMute = () => {
    if (videoRef.current) {
      const newMutedState = !isMuted;
      videoRef.current.muted = newMutedState;
      setIsMuted(newMutedState);
      if (autoplayBlocked && !newMutedState) {
        setAutoplayBlocked(false);
      }
    }
  };
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  return <div className="min-h-screen bg-[#FFF9F2] flex flex-col items-center justify-center p-4 sm:p-8 relative overflow-hidden font-sans" data-id="element-553">
      {/* Vibrant Background Elements */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-orange-300/20 blur-[100px] pointer-events-none" data-id="element-554" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-amber-300/20 blur-[120px] pointer-events-none" data-id="element-555" />
      <div className="absolute top-[20%] right-[10%] w-[30%] h-[30%] rounded-full bg-rose-300/10 blur-[80px] pointer-events-none" data-id="element-556" />

      {/* Ambient floating icons */}
      <FloatingIcon delay={0} x="5%" y="15%" duration={9} data-id="element-557">
        <Calculator className="w-10 h-10 sm:w-14 sm:h-14" data-id="element-558" />
      </FloatingIcon>
      <FloatingIcon delay={1.5} x="88%" y="10%" duration={11} data-id="element-559">
        <BookOpen className="w-8 h-8 sm:w-12 sm:h-12" data-id="element-560" />
      </FloatingIcon>
      <FloatingIcon delay={2.2} x="85%" y="75%" duration={8} data-id="element-561">
        <Sparkles className="w-9 h-9 sm:w-12 sm:h-12" data-id="element-562" />
      </FloatingIcon>

      <div className="flex flex-col lg:flex-row items-center justify-center gap-12 lg:gap-24 w-full max-w-7xl relative z-10" data-id="element-563">
        {/* Left Column: Content & CTAs */}
        <div className="w-full max-w-lg flex flex-col items-center lg:items-start text-center lg:text-left" data-id="element-564">
          <motion.div className="mb-8" initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} data-id="element-565">
            <DjayMeesterLogo size="xl" data-id="element-566" />
          </motion.div>

          <motion.div className="mb-8 relative" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} data-id="element-567">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight leading-[1.1] mb-4" data-id="element-568">
              Leren wordt een{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500" data-id="element-569">
                groot avontuur!
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 font-medium max-w-md mx-auto lg:mx-0" data-id="element-570">
              Oefen rekenen, taal, spelling en meer — helemaal op jouw eigen
              niveau met Meester Djay.
            </p>
          </motion.div>

          <motion.div className="w-full space-y-4 max-w-md" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4,
          duration: 0.5
        }} data-id="element-571">
            <div className="relative group" data-id="element-572">
              <div className="absolute -inset-1 bg-gradient-to-r from-orange-400 to-amber-400 rounded-2xl blur opacity-40 group-hover:opacity-70 transition duration-300" data-id="element-573"></div>
              <Button variant="primary" size="xl" className="relative w-full text-lg shadow-xl" onClick={onRegister} data-id="element-574">
                Start je avontuur
                <ArrowRight className="w-6 h-6 ml-2" data-id="element-575" />
              </Button>
            </div>

            <div className="flex flex-col sm:flex-row gap-4" data-id="element-576">
              <Button variant="outline" size="lg" className="w-full sm:w-1/2 bg-white/80 backdrop-blur-sm border-orange-200 text-orange-700 hover:bg-orange-50 hover:border-orange-300" onClick={onLoadDemo} data-id="element-577">
                <Sparkles className="w-5 h-5 mr-2" data-id="element-578" />
                Probeer demo
              </Button>
              <Button variant="outline" size="lg" className="w-full sm:w-1/2 bg-white/80 backdrop-blur-sm border-slate-200 text-slate-700 hover:bg-slate-50" onClick={onLogin} data-id="element-579">
                Log in
              </Button>
            </div>
          </motion.div>

          <motion.div className="mt-8" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.7
        }} data-id="element-580">
            <p className="text-sm text-slate-500 font-medium flex items-center gap-2 bg-white/60 backdrop-blur-md py-2.5 px-5 rounded-full border border-slate-200/60 shadow-sm" data-id="element-581">
              <Lock className="w-4 h-4 text-emerald-500" data-id="element-582" />
              100% Veilig & lokaal opgeslagen
            </p>
          </motion.div>
        </div>

        {/* Right Column: Video & Mascot */}
        <motion.div className="w-full max-w-md lg:max-w-lg relative" initial={{
        opacity: 0,
        x: 40
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.3,
        duration: 0.7,
        type: 'spring',
        bounce: 0.4
      }} data-id="element-583">
          {/* Djay Mascot peeking out */}
          <div className="absolute -top-16 -left-12 z-20 hidden sm:block" data-id="element-584">
            <motion.div animate={{
            y: [0, -8, 0],
            rotate: [0, -5, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-585">
              <DjayAvatar size="lg" emotion={welcomeMsg.emotion} animate={true} data-id="element-586" />
            </motion.div>
          </div>

          <div className="absolute -top-24 left-16 z-20 hidden sm:block" data-id="element-587">
            <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.8,
            duration: 0.4
          }} data-id="element-588">
              <DjayBubble message={welcomeMsg.text} position="right" autoDismissMs={0} colorScheme="amber" data-id="element-589" />
            </motion.div>
          </div>

          {/* Video Player Container */}
          <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white bg-orange-100 group" data-id="element-590">
            <div className="absolute inset-0 bg-gradient-to-tr from-orange-900/10 to-transparent pointer-events-none z-10" data-id="element-591" />

            {!hasVideoError ? <video ref={videoRef} src="https://cdn.magicpatterns.com/uploads/qpe4tybRX3NWaXWg6AM6tr/gemini_generated_video_ffdfec60.mp4" loop playsInline className="w-full h-auto object-cover aspect-[9/16] sm:aspect-auto" onClick={togglePlay} onError={() => {
            debug('Video failed to load, falling back to illustration.');
            setHasVideoError(true);
          }} data-id="element-592" /> : <div className="w-full aspect-[9/16] sm:aspect-video bg-gradient-to-br from-orange-100 to-amber-200 flex flex-col items-center justify-center p-8" data-id="element-593">
                <motion.div animate={{
              y: [0, -10, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-594">
                  <DjayAvatar size="xl" emotion="excited" animate={true} data-id="element-595" />
                </motion.div>
                <div className="mt-6 text-center" data-id="element-596">
                  <h3 className="text-xl font-bold text-orange-900 mb-2" data-id="element-597">
                    Klaar voor de start!
                  </h3>
                  <p className="text-orange-800/80 font-medium" data-id="element-598">
                    Jouw leeravontuur begint hier.
                  </p>
                </div>
              </div>}

            {/* Video Controls Overlay */}
            {!hasVideoError && <div className="absolute bottom-4 right-4 z-30 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-599">
                <button onClick={togglePlay} className="bg-black/50 hover:bg-black/70 text-white p-2.5 rounded-full backdrop-blur-sm transition-colors" aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-600">
                  {isPlaying ? <Pause className="w-5 h-5" data-id="element-601" /> : <Play className="w-5 h-5 ml-0.5" data-id="element-602" />}
                </button>
                <button onClick={toggleMute} className="bg-black/50 hover:bg-black/70 text-white p-2.5 rounded-full backdrop-blur-sm transition-colors" aria-label={isMuted ? 'Unmute' : 'Mute'} data-id="element-603">
                  {isMuted ? <VolumeX className="w-5 h-5" data-id="element-604" /> : <Volume2 className="w-5 h-5" data-id="element-605" />}
                </button>
              </div>}

            {/* Autoplay Blocked Overlay */}
            {autoplayBlocked && <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/20 backdrop-blur-[2px]" data-id="element-606">
                <button onClick={toggleMute} className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full font-bold flex items-center gap-2 shadow-lg transform transition hover:scale-105" data-id="element-607">
                  <Volume2 className="w-5 h-5" data-id="element-608" />
                  Zet geluid aan
                </button>
              </div>}
          </div>
        </motion.div>
      </div>
    </div>;
}